import { useState } from 'react';
import { bulkUploadSales, bulkUploadSalesReports, bulkUploadSalesReturns } from '../api/sales';
import { useAuth } from '../context/AuthContext';
import { downloadCsvTemplate } from '../utils/downloadTemplate';

const SIMPLE_CSV_COLUMNS = ['date', 'customerCode', 'productCode', 'salesmanCode', 'qty', 'revenue', 'cost'];

export default function SalesUpload() {
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return (
      <div>
        <h1 className="text-xl font-semibold text-gray-800 mb-4">Sales Transactions Upload</h1>
        <p className="text-sm text-gray-400">Only admins can upload sales data.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-800 mb-4">Sales Transactions Upload</h1>
      <ReportJoinUpload
        title="Key Sales & HORECA Upload (recommended)"
        description="Upload the combined Key Sales/HORECA ERP export pair — matched by Invoice Number. Missing Customers, Salesmen, and Products are created automatically from the report data. These files don't carry Channel or Sub-Channel, so each transaction picks those up automatically from the Customer Master by matching Customer Name — assign them there beforehand if you want them populated."
        buttonLabel="Import & Join Reports"
        clearChannelNot="Van Sales"
        clearScopeLabel="Key Sales and HORECA"
      />
      <ReportJoinUpload
        title="Van Sales Upload"
        description="Upload the separate Van Sales ERP export pair, in the same format as above — matched by Invoice Number. Use this when Van Sales data comes as its own file rather than the combined Key Sales/HORECA report. Channel/Sub-Channel are still picked up from the Customer Master by matching Customer Name."
        buttonLabel="Import Van Sales Reports"
        clearChannel="Van Sales"
        clearScopeLabel="Van Sales"
      />
      <SalesReturnUpload />
      <SimpleCsvUpload />
    </div>
  );
}

function MonthPicker({ month, onChange }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-medium text-gray-600 mb-1">Report Month (optional)</label>
      <input
        type="month"
        value={month}
        onChange={(e) => onChange(e.target.value)}
        className="border border-gray-300 rounded px-3 py-1.5 text-sm"
      />
      <p className="text-xs text-gray-500 mt-1">
        Tags every imported transaction with this month, enabling month-wise analysis and comparison on
        the Dashboard. Leave blank to derive the month from each transaction's own date instead — e.g.
        when uploading a whole year's invoices in one file, leave this blank and each transaction is
        bucketed into its own month automatically.
      </p>
    </div>
  );
}

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 8 }, (_, i) => String(CURRENT_YEAR + 1 - i));

function YearPicker({ year, onChange }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-medium text-gray-600 mb-1">Year (for "Clear existing data" scope)</label>
      <select
        value={year}
        onChange={(e) => onChange(e.target.value)}
        className="border border-gray-300 rounded px-3 py-1.5 text-sm"
      >
        <option value="">Any year</option>
        {YEAR_OPTIONS.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
      <p className="text-xs text-gray-500 mt-1">
        Only used to scope "Clear existing data" below to one calendar year. Doesn't affect which month
        each transaction is tagged with — that's still driven by Report Month above (or each row's own
        date if left blank).
      </p>
    </div>
  );
}

function ClearExistingOption({ checked, onChange, month, year, clearScopeLabel }) {
  const scope = clearScopeLabel ? `${clearScopeLabel} data only` : 'every division’s data';
  const scopeDescription = month
    ? `already tagged "${month}"`
    : year
      ? `dated anywhere in ${year}`
      : null;
  return (
    <label className="flex items-start gap-2 text-sm text-gray-700 mb-4">
      <input type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        Clear existing data before importing
        <span className="block text-xs text-gray-500">
          {scopeDescription
            ? `Deletes only existing ${scope} ${scopeDescription}, then loads this file's data in their place. Safe to re-import data you've already uploaded.`
            : `No month or year selected above, so this deletes ALL existing ${scope} across every date before importing. Select a month or year above to scope this instead.`}
          {' '}Customers, Products, and Salesmen masters are never affected.
        </span>
      </span>
    </label>
  );
}

function confirmClear(month, clearScopeLabel, year) {
  const scope = clearScopeLabel ? `${clearScopeLabel} data` : 'ALL sales transactions';
  const scopeDescription = month ? `tagged "${month}"` : year ? `dated anywhere in ${year}` : null;
  const message = scopeDescription
    ? `This will permanently delete existing ${scope} ${scopeDescription} before importing. Continue?`
    : `No month or year is selected, so this will permanently delete ${scope} across every date before importing. Continue?`;
  return window.confirm(message);
}

function ReportJoinUpload({ title, description, buttonLabel, clearChannel, clearChannelNot, clearScopeLabel }) {
  const [summaryFile, setSummaryFile] = useState(null);
  const [itemFiles, setItemFiles] = useState([]);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [clearExisting, setClearExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  async function handleUpload() {
    if (!summaryFile || itemFiles.length === 0) return;
    if (clearExisting && !confirmClear(month, clearScopeLabel, year)) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await bulkUploadSalesReports(summaryFile, itemFiles, {
        month,
        year,
        clearExisting,
        clearChannel,
        clearChannelNot,
      });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-2xl mb-6">
      <h2 className="text-sm font-semibold text-gray-800 mb-1">{title}</h2>
      <p className="text-sm text-gray-600 mb-4">{description}</p>

      <div className="grid grid-cols-1 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Sales Engineer Wise Profit Details (Sales Engineer, Customer, Invoice totals)
          </label>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setSummaryFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-700 border border-gray-300 rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Invoice Wise Product Details (Part Number, Description per line item)
          </label>
          <input
            type="file"
            accept=".xlsx,.xls"
            multiple
            onChange={(e) => setItemFiles(Array.from(e.target.files || []))}
            className="block w-full text-sm text-gray-700 border border-gray-300 rounded px-3 py-2"
          />
          <p className="text-xs text-gray-500 mt-1">
            If your ERP caps how many invoices a single Item report export can contain, export it in
            smaller date-range chunks and select all of them here (Ctrl/Cmd-click, or drag-select) — they're
            merged together and matched against the one Sales Engineer report above.
            {itemFiles.length > 0 && ` ${itemFiles.length} file(s) selected.`}
          </p>
        </div>
      </div>

      <MonthPicker month={month} onChange={setMonth} />
      <YearPicker year={year} onChange={setYear} />
      <ClearExistingOption
        checked={clearExisting}
        onChange={setClearExisting}
        month={month}
        year={year}
        clearScopeLabel={clearScopeLabel}
      />

      {error && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      {result && (
        <div className="mb-4 text-sm bg-gray-50 border border-gray-200 rounded p-3 max-h-64 overflow-auto">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700 mb-2">
            {result.cleared > 0 && (
              <div className="col-span-2 font-medium text-amber-700">
                Cleared {result.cleared} existing {clearScopeLabel || ''} transactions{month ? ` for ${month}` : ''}
              </div>
            )}
            <div>Invoices in Sales Engineer report: {result.invoicesInSummaryReport}</div>
            <div>Invoices in Item report: {result.invoicesInItemReport}</div>
            <div className="font-medium text-green-700">Invoices imported: {result.invoicesImported}</div>
            <div className="font-medium text-green-700">Line items inserted: {result.itemsInserted}</div>
            {result.salesReturnsImported > 0 && (
              <div className="text-amber-700">
                Of which Sales Returns: {result.salesReturnsImported} (excluded from main figures)
              </div>
            )}
            {result.noItemDetail > 0 && (
              <div className="col-span-2 text-amber-700">
                No item detail found: {result.noItemDetail} invoices imported using invoice-level totals
                only (no Part Number/Quantity — the Item report had no matching entry for these).
              </div>
            )}
            <div>New customers created: {result.customersCreated}</div>
            <div>New products created: {result.productsCreated}</div>
            {result.productsUnitBackfilled > 0 && (
              <div>Product units filled in: {result.productsUnitBackfilled}</div>
            )}
            <div>New salesmen created: {result.salesmenCreated}</div>
            <div className={result.failed ? 'font-medium text-red-600' : ''}>Failed: {result.failed}</div>
          </div>
          <ReviewList
            title={`No item detail (invoice-level totals only) — ${result.noItemDetail} total`}
            rows={result.noItemDetailInvoices}
            columns={[
              ['invoiceNumber', 'Invoice'],
              ['customerName', 'Customer'],
              ['netAmount', 'Net Amount'],
            ]}
          />
          {result.errors?.length > 0 && (
            <ul className="list-disc list-inside text-red-600 space-y-0.5">
              {result.errors.slice(0, 50).map((e, i) => (
                <li key={i}>
                  Invoice {e.row}: {e.message}
                </li>
              ))}
              {result.errors.length > 50 && <li>…and {result.errors.length - 50} more</li>}
            </ul>
          )}
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!summaryFile || itemFiles.length === 0 || loading}
        className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white"
      >
        {loading ? 'Importing…' : buttonLabel}
      </button>
    </div>
  );
}

function ReviewList({ title, rows, columns, rowShape }) {
  if (!rows?.length) return null;
  const shaped = rowShape ? rows.map(rowShape) : rows;
  return (
    <details className="mb-3">
      <summary className="font-medium text-amber-700 cursor-pointer">{title}</summary>
      <div className="mt-1 max-h-48 overflow-auto border border-gray-200 rounded">
        <table className="w-full text-xs border-collapse">
          <thead className="sticky top-0 bg-gray-100">
            <tr className="text-left text-gray-500">
              {columns.map(([key, label]) => (
                <th key={key} className="px-2 py-1">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shaped.map((row, i) => (
              <tr key={i} className="border-t border-gray-200">
                {columns.map(([key]) => (
                  <td key={key} className="px-2 py-1">
                    {row[key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length >= 200 && (
        <p className="text-xs text-gray-400 mt-1">Showing the first 200 — re-run with narrower data to see more.</p>
      )}
    </details>
  );
}

function SalesReturnUpload() {
  const [file, setFile] = useState(null);
  const [year, setYear] = useState('');
  const [clearExisting, setClearExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  async function handleUpload() {
    if (!file) return;
    const scopeDescription = year ? `dated anywhere in ${year}` : 'across every date';
    if (
      clearExisting &&
      !window.confirm(`This will permanently delete every previously-applied return deduction ${scopeDescription} before re-importing. Continue?`)
    )
      return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await bulkUploadSalesReturns(file, { year, clearExisting });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-2xl mb-6">
      <h2 className="text-sm font-semibold text-gray-800 mb-1">Sales Return Upload</h2>
      <p className="text-sm text-gray-600 mb-4">
        Upload the "Sales Return Detail" ERP export as a single file — the month is automatically
        detected per return line from its own Return Date. Matching goes in order:{' '}
        <strong>Invoice Number + Product</strong> first; if that doesn't match, falls back to{' '}
        <strong>Customer Name + Product</strong>, searching backward from the return's own month for
        the most recent matching sale (a January sale can still be netted against a July return).
        Either way, the return's <strong>Unit</strong> must match the sale's unit exactly
        (CTN against CTN, PCS against PCS — never converted). A unit mismatch is{' '}
        <strong>not</strong> deducted and is listed separately below for manual review instead.
      </p>

      <input
        type="file"
        accept=".xlsx,.xls"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="block w-full text-sm text-gray-700 border border-gray-300 rounded px-3 py-2 mb-4"
      />

      <YearPicker year={year} onChange={setYear} />

      <label className="flex items-start gap-2 text-sm text-gray-700 mb-4">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={clearExisting}
          onChange={(e) => setClearExisting(e.target.checked)}
        />
        <span>
          Clear existing return deductions before importing
          <span className="block text-xs text-gray-500">
            {year
              ? `Deletes only previously-applied return deductions dated anywhere in ${year}, then re-applies from this file.`
              : 'No year selected above, so this deletes ALL previously-applied return deductions across every date, then re-applies from this file.'}
            {' '}Safe to re-import the same or an updated return report. Sales transactions themselves are
            never deleted, only the deduction rows.
          </span>
        </span>
      </label>

      {error && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      {result && (
        <div className="mb-4 text-sm bg-gray-50 border border-gray-200 rounded p-3 max-h-72 overflow-auto">
          {result.cleared > 0 && (
            <div className="font-medium text-amber-700 mb-1">
              Cleared {result.cleared} previously-applied return deductions
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700 mb-2">
            <div>Total return lines in file: {result.total}</div>
            <div className="font-medium text-green-700">
              Deducted: {result.deducted} (Invoice: {result.deductedViaInvoice}, Customer Name:{' '}
              {result.deductedViaCustomerName})
            </div>
            <div>Ignored (customer not found): {result.ignoredNoCustomerMatch}</div>
            <div>Ignored (no matching sale found): {result.ignoredNoSaleFound}</div>
            <div className="text-amber-700">Unit mismatch (not deducted): {result.unitMismatch}</div>
            {result.alreadyApplied > 0 && <div>Already applied previously: {result.alreadyApplied}</div>}
          </div>
          <ReviewList
            title={`Unit mismatches (not deducted) — ${result.unitMismatch} total`}
            rows={result.unitMismatches}
            columns={[
              ['customerName', 'Customer'],
              ['product', 'Product'],
              ['soldUnits', 'Sold Unit(s)'],
              ['returnUnit', 'Returned Unit'],
              ['returnQty', 'Return Qty'],
            ]}
            rowShape={(m) => ({ ...m, product: `${m.productCode} ${m.productName}` })}
          />
          <ReviewList
            title={`Customer not found — ${result.ignoredNoCustomerMatch} total`}
            rows={result.noCustomerMatches}
            columns={[
              ['rawCustomer', 'Raw Customer (from file)'],
              ['product', 'Product'],
              ['unit', 'Unit'],
              ['returnQty', 'Return Qty'],
              ['returnNumber', 'Return Number'],
            ]}
            rowShape={(m) => ({ ...m, product: `${m.productCode} ${m.productName}` })}
          />
          <ReviewList
            title={`No matching sale found — ${result.ignoredNoSaleFound} total`}
            rows={result.noSaleFoundMatches}
            columns={[
              ['customerName', 'Customer'],
              ['product', 'Product'],
              ['unit', 'Unit'],
              ['returnQty', 'Return Qty'],
              ['checkedMonths', 'Months Checked'],
              ['returnNumber', 'Return Number'],
            ]}
            rowShape={(m) => ({ ...m, product: `${m.productCode} ${m.productName}` })}
          />
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!file || loading}
        className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white"
      >
        {loading ? 'Importing…' : 'Import Sales Returns'}
      </button>
    </div>
  );
}

function SimpleCsvUpload() {
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [clearExisting, setClearExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  async function handleUpload() {
    if (!file) return;
    if (clearExisting && !confirmClear(month, undefined, year)) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await bulkUploadSales(file, { month, year, clearExisting });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-2xl">
      <h2 className="text-sm font-semibold text-gray-800 mb-1">Simple CSV/Excel Upload</h2>
      <p className="text-sm text-gray-600 mb-4">
        For manually prepared transaction files. Each row must reference an existing Customer,
        Product, and Salesman code — upload those masters first if you haven't already.
      </p>
      <p className="text-xs text-gray-500 mb-2 bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono">
        Columns: date, customerCode, productCode, salesmanCode, qty, revenue, cost
      </p>
      <button
        type="button"
        onClick={() => downloadCsvTemplate('simple-sales-upload-template.csv', SIMPLE_CSV_COLUMNS)}
        className="mb-4 text-xs text-blue-600 hover:underline"
      >
        Download blank template (CSV)
      </button>

      <input
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="block w-full text-sm text-gray-700 border border-gray-300 rounded px-3 py-2 mb-4"
      />

      <MonthPicker month={month} onChange={setMonth} />
      <YearPicker year={year} onChange={setYear} />
      <ClearExistingOption checked={clearExisting} onChange={setClearExisting} month={month} year={year} />

      {error && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      {result && (
        <div className="mb-4 text-sm bg-gray-50 border border-gray-200 rounded p-3 max-h-56 overflow-auto">
          {result.cleared > 0 && (
            <div className="font-medium text-amber-700 mb-1">
              Cleared {result.cleared} existing transactions{month ? ` for ${month}` : ''}
            </div>
          )}
          <div className="font-medium text-gray-800 mb-1">
            Total: {result.total} · Inserted: {result.inserted} · Failed: {result.failed}
          </div>
          {result.errors?.length > 0 && (
            <ul className="list-disc list-inside text-red-600 space-y-0.5">
              {result.errors.map((e, i) => (
                <li key={i}>
                  Row {e.row}: {e.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!file || loading}
        className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white"
      >
        {loading ? 'Uploading…' : 'Upload Sales File'}
      </button>
    </div>
  );
}
